import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import ThreadsMobilePreview from '../ThreadsMobilePreview';
import { useToast } from '../ui';

/**
 * PreviewTabs - Tab navigation component for Preview and Go Live modes
 *
 * Features:
 * - Two tabs: Preview and Go Live
 * - Mobile preview in Preview tab
 * - Camera recording in Go Live tab
 * - Smooth tab switching
 *
 * @param {object} props
 * @param {string} props.content - Generated content to display
 * @param {string} props.accountName - Account name for preview
 * @param {boolean} props.loading - Whether content is loading
 * @param {boolean} props.hasContent - Whether content has been generated
 * @param {function} props.onEdit - Callback for Edit Post button
 * @param {function} props.onPublish - Callback for Publish Now button
 * @param {boolean} props.publishing - Whether currently publishing
 * @param {function} props.onRecordingComplete - Callback when recording is completed
 */
function PreviewTabs({
  content = '',
  accountName = 'Your Account',
  loading = false,
  hasContent = false,
  onEdit,
  onPublish,
  publishing = false,
  onRecordingComplete,
  isMobileRecordMode = false
}) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(isMobileRecordMode ? 'golive' : 'preview');

  // Camera states
  const [isRecording, setIsRecording] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Teleprompter & Recording states
  const [countdown, setCountdown] = useState(null); // 5, 4, 3, 2, 1, null
  const [scriptSegments, setScriptSegments] = useState([]); // Array of script sentences
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [isPreparingNext, setIsPreparingNext] = useState(false); // 5秒準備時間
  const [preparationCountdown, setPreparationCountdown] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [recordingState, setRecordingState] = useState('idle'); // 'idle', 'countdown', 'recording', 'paused', 'preparing', 'completed'
  const [isPaused, setIsPaused] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const pauseStartTimeRef = useRef(null);
  const remainingTimeRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const preparationTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Fixed to 9:16 vertical format for recording (camera is already horizontal)
  const aspectRatio = '9:16';

  // Subtitle - use current segment content during recording/playback
  const [recordedSegments, setRecordedSegments] = useState([]);
  const [savedSegmentMarkers, setSavedSegmentMarkers] = useState([]); // Persistent storage for segment markers
  const [playbackSegmentIndex, setPlaybackSegmentIndex] = useState(0);
  const videoPlaybackRef = useRef(null);

  // Use isMobileRecordMode prop if provided, otherwise detect mobile
  const isMobile = isMobileRecordMode || window.innerWidth < 768;

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError(null);
      setIsCameraActive(false); // Reset first

      // Request 9:16 vertical video (1080x1920 for high quality)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1080 },
          height: { ideal: 1920 }
        },
        audio: true // Enable microphone for recording
      });

      streamRef.current = stream;
      setIsCameraActive(true);

      // Setup voice detection
      setupVoiceDetection(stream);

      // Use retry mechanism to ensure DOM is updated
      const attachStreamToVideo = (retryCount = 0) => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;

          // Force play
          videoRef.current.play().catch(err => {
            console.error('❌ Video play error:', err);
          });
        } else if (retryCount < 10) {
          // Retry after a short delay (up to 10 times)
          setTimeout(() => attachStreamToVideo(retryCount + 1), 100);
        } else {
          console.warn('⚠️ videoRef not ready after retries, will attach when available');
        }
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          attachStreamToVideo();
        });
      });

    } catch (error) {
      console.error('❌ Camera access error:', error);
      setCameraError('無法訪問相機，請確認瀏覽器權限設置');
      setIsCameraActive(false);
    }
  };

  // Setup voice activity detection
  const setupVoiceDetection = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.smoothingTimeConstant = 0.8;
      analyser.fftSize = 1024;

      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

    } catch (error) {
      console.error('❌ Voice detection setup error:', error);
    }
  };

  // Check if user is speaking
  const checkVoiceActivity = () => {
    if (!analyserRef.current) return false;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average volume
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;

    // Threshold for speech detection (adjust as needed)
    const SPEECH_THRESHOLD = 30;

    return average > SPEECH_THRESHOLD;
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setIsRecording(false);
  };

  // Toggle recording
  const toggleRecording = () => {
    if (!isCameraActive) {
      startCamera();
    } else {
      stopCamera();
    }
  };

  // Parse script into segments (sentences) - Extract dialogue lines or meaningful content
  const parseScript = (scriptText) => {
    if (!scriptText || scriptText.trim() === '') return [];

    console.log('📝 Parsing script:', scriptText.substring(0, 200));

    const dialogueLines = [];
    const lines = scriptText.split('\n');

    // Method 1: Extract lines with "台詞" label followed by content in quotes
    for (const line of lines) {
      // Match: **台詞** (旁白)："當你肚子餓了，這種感覺..."
      // or: - **台詞**："..."
      const labeledDialogueMatch = line.match(/台[詞词]\*{0,2}\s*(?:\([^)]*\))?\s*[:：]\s*[「『"]([^」』"]+)[」』"]/);
      if (labeledDialogueMatch && labeledDialogueMatch[1].trim()) {
        dialogueLines.push(labeledDialogueMatch[1].trim());
        continue;
      }
    }

    // Method 2: Extract any content in quotes (Chinese or English quotes)
    if (dialogueLines.length === 0) {
      for (const line of lines) {
        // Try Chinese quotes first
        const chineseQuoteMatch = line.match(/[「『"](.*?)[」』"]/);
        if (chineseQuoteMatch && chineseQuoteMatch[1].trim()) {
          const content = chineseQuoteMatch[1].trim();
          if (!content.includes('###') && !content.includes('**總時長**') && content.length > 5) {
            dialogueLines.push(content);
            continue;
          }
        }
        // Try English double quotes
        const englishQuoteMatch = line.match(/"([^"]+)"/);
        if (englishQuoteMatch && englishQuoteMatch[1].trim()) {
          const content = englishQuoteMatch[1].trim();
          if (content.length > 5) {
            dialogueLines.push(content);
          }
        }
      }
    }

    // Method 3: Look for lines that start with dialogue markers (-, •, etc.) and contain quotes
    if (dialogueLines.length === 0) {
      for (const line of lines) {
        if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
          const quoteMatch = line.match(/[「『""](.*?)[」』""]/);
          if (quoteMatch && quoteMatch[1].trim()) {
            dialogueLines.push(quoteMatch[1].trim());
          }
        }
      }
    }

    // Method 4 (NEW): Fallback - split entire script into meaningful chunks
    // This handles cases where there are no quotes in the script
    if (dialogueLines.length === 0) {
      console.log('⚠️ No quoted dialogue found, using fallback method');

      // Remove markdown headers, timestamps, and other metadata
      const cleanLines = lines
        .map(line => line.trim())
        .filter(line => {
          // Skip empty lines
          if (!line) return false;
          // Skip markdown headers
          if (line.startsWith('#')) return false;
          // Skip lines that look like metadata (timestamps, section labels)
          if (line.match(/^\d+-?\d*秒/)) return false;
          if (line.match(/^[\*\-]\s*\*\*/)) return false;
          // Skip very short lines
          if (line.length < 10) return false;
          return true;
        })
        .map(line => {
          // Remove markdown formatting
          return line
            .replace(/\*\*([^*]+)\*\*/g, '$1')
            .replace(/\*([^*]+)\*/g, '$1')
            .replace(/^[-•]\s*/, '')
            .trim();
        })
        .filter(line => line.length > 0);

      // Take meaningful lines as dialogue
      for (const line of cleanLines) {
        if (line.length > 10 && !line.includes('---')) {
          dialogueLines.push(line);
        }
      }
    }

    // Method 5 (NEW): Ultimate fallback - just split the entire text into sentences
    if (dialogueLines.length === 0) {
      console.log('⚠️ Still no content, using ultimate fallback');
      const allText = scriptText
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/#+\s*/g, '')
        .replace(/[-•]\s*/g, '');

      const sentences = allText
        .split(/[。！？.!?\n]+/)
        .map(s => s.trim())
        .filter(s => s.length > 10);

      for (const sentence of sentences.slice(0, 20)) { // Limit to 20 segments
        dialogueLines.push(sentence);
      }
    }

    // Split each dialogue into segments
    const segments = [];
    for (const dialogue of dialogueLines) {
      // If the dialogue is long, split it into smaller chunks
      if (dialogue.length > 50) {
        const sentences = dialogue
          .split(/[。！？，,]+/)
          .map(s => s.trim())
          .filter(s => s.length > 3);

        for (const sentence of sentences) {
          segments.push({
            text: sentence,
            duration: Math.ceil(sentence.length / 3) // 1秒 = 3個字
          });
        }
      } else {
        segments.push({
          text: dialogue,
          duration: Math.ceil(dialogue.length / 3)
        });
      }
    }

    console.log('✅ Parsed segments:', segments.length, segments.map(s => s.text.substring(0, 30)));
    return segments;
  };

  // Initialize script segments when content changes
  useEffect(() => {
    if (content && content.trim()) {
      const segments = parseScript(content);
      setScriptSegments(segments);
      setCurrentSegmentIndex(0);
    }
  }, [content]);

  // Start recording flow
  const startRecordingFlow = async () => {
    if (!streamRef.current) {
      await startCamera();
      // Wait for camera to be ready
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    if (!streamRef.current) {
      toast.warning('相機未就緒，請重試');
      return;
    }

    if (scriptSegments.length === 0) {
      toast.warning('請先生成腳本內容');
      return;
    }

    setRecordedChunks([]);
    setRecordedVideoUrl(null);
    setCurrentSegmentIndex(0);
    setRecordingState('countdown');

    // Initialize MediaRecorder with single instance
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 2500000
    });

    const allChunks = [];
    const segmentMarkers = []; // Track when each segment starts/ends

    mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        allChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(allChunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);

      console.log('🎬 Recording complete:', {
        segments: segmentMarkers.length,
        videoUrl: url,
        blobSize: blob.size,
        markers: segmentMarkers
      });

      // Save segment markers to persistent state for subtitles
      setSavedSegmentMarkers(segmentMarkers);

      // Stop camera to show recorded video
      stopCamera();

      setRecordedVideoUrl(url);
      setRecordingState('completed');

      // Pass recording data to parent component
      if (onRecordingComplete) {
        const videoData = {
          videoUrl: url,
          aspectRatio: aspectRatio,
          blob: blob
        };
        onRecordingComplete(videoData);
      }
    };

    mediaRecorderRef.current = {
      recorder: mediaRecorder,
      chunks: allChunks,
      segmentMarkers: segmentMarkers,
      state: 'inactive'
    };

    // Start countdown (5 seconds)
    startCountdown();
  };

  // Countdown before recording
  const startCountdown = () => {
    let count = 3; // Changed from 5 to 3 seconds
    setCountdown(count);

    const timer = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        setCountdown(null);
        clearInterval(timer);
        countdownTimerRef.current = null;
        // Start recording first segment
        recordSegment(0);
      }
    }, 1000);
    countdownTimerRef.current = timer; // Store timer ID for cleanup
  };

  // Record a specific segment
  const recordSegment = async (segmentIndex) => {
    if (segmentIndex >= scriptSegments.length) {
      // All segments recorded - stop recording
      const recorder = mediaRecorderRef.current?.recorder;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
      return;
    }

    const segment = scriptSegments[segmentIndex];

    setCurrentSegmentIndex(segmentIndex);
    setRecordingState('recording');
    setIsRecording(true);
    setIsPaused(false);

    const recorder = mediaRecorderRef.current?.recorder;

    // Don't start recording immediately - let voice detection control it
    // Recording will start when voice is detected in the voice activity detection loop

    // Mark segment start time
    const startTime = Date.now();
    mediaRecorderRef.current.segmentMarkers.push({
      segmentIndex,
      startTime,
      text: segment.text
    });

    // Store the full duration for this segment
    remainingTimeRef.current = segment.duration * 1000;

    // Voice activity detection variables
    let silenceStartTime = null;
    const SILENCE_DURATION = 1500; // 1.5 seconds of silence
    const MAX_RECORDING_TIME = segment.duration * 1000 + 5000; // Max time: estimated + 5 seconds buffer
    let hasSpoken = false;
    let isCurrentlyRecording = false;

    // Function to end segment recording
    const endSegmentRecording = () => {
      // Clear timers
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearInterval(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      // Pause recording for preparation
      if (recorder.state === 'recording') {
        recorder.pause();
      }
      setIsRecording(false);
      setIsSpeaking(false);
      remainingTimeRef.current = null;

      // Mark segment end time
      const endTime = Date.now();
      const lastMarker = mediaRecorderRef.current.segmentMarkers[mediaRecorderRef.current.segmentMarkers.length - 1];
      if (lastMarker) {
        lastMarker.endTime = endTime;
        lastMarker.duration = endTime - startTime;
      }

      // Check if this is the last segment
      const isLastSegment = (segmentIndex + 1) >= scriptSegments.length;

      if (isLastSegment) {
        // Last segment - finish recording immediately without preparation countdown
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      } else {
        // Not the last segment - start preparation countdown for next segment
        startPreparationCountdown(segmentIndex);
      }
    };

    // Voice activity detection loop - Smart recording (only when speaking)
    silenceTimerRef.current = setInterval(() => {
      const isSpeakingNow = checkVoiceActivity();
      setIsSpeaking(isSpeakingNow);

      if (isSpeakingNow) {
        // User is speaking
        hasSpoken = true;
        silenceStartTime = null; // Reset silence timer

        // Resume recording if paused (only record when speaking)
        if (!isCurrentlyRecording && recorder.state === 'paused') {
          recorder.resume();
          isCurrentlyRecording = true;
        } else if (recorder.state === 'inactive') {
          recorder.start(100);
          isCurrentlyRecording = true;
        }
      } else {
        // User is not speaking
        if (isCurrentlyRecording && recorder.state === 'recording') {
          // Pause recording during silence to skip silent parts
          recorder.pause();
          isCurrentlyRecording = false;
        }

        if (hasSpoken && !isSpeakingNow) {
          // User has stopped speaking - start counting silence duration
          if (silenceStartTime === null) {
            silenceStartTime = Date.now();
          } else {
            const silenceDuration = Date.now() - silenceStartTime;
            if (silenceDuration >= SILENCE_DURATION) {
              endSegmentRecording();
            }
          }
        }
      }
    }, 100); // Check every 100ms

    // Maximum recording time safety timeout
    recordingTimerRef.current = setTimeout(() => {
      endSegmentRecording();
    }, MAX_RECORDING_TIME);
  };


  // Preparation countdown between segments (3 seconds)
  const startPreparationCountdown = (completedSegmentIndex) => {
    setRecordingState('preparing');
    setIsPreparingNext(true);
    let count = 3; // Changed from 5 to 3 seconds
    setPreparationCountdown(count);

    const timer = setInterval(() => {
      count--;
      if (count > 0) {
        setPreparationCountdown(count);
      } else {
        setPreparationCountdown(null);
        setIsPreparingNext(false);
        clearInterval(timer);
        preparationTimerRef.current = null;

        // Record next segment
        const nextIndex = completedSegmentIndex + 1;
        recordSegment(nextIndex);
      }
    }, 1000);
    preparationTimerRef.current = timer; // Store timer ID for cleanup
  };

  // Pause recording
  const pauseRecording = () => {
    const recorder = mediaRecorderRef.current?.recorder;
    if (recorder && recorder.state === 'recording') {
      recorder.pause();
      setIsPaused(true);
      setRecordingState('paused');

      // Save remaining time
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        pauseStartTimeRef.current = Date.now();
      }
    }
  };

  // Resume recording
  const resumeRecording = () => {
    const recorder = mediaRecorderRef.current?.recorder;
    if (recorder && recorder.state === 'paused') {
      recorder.resume();
      setIsPaused(false);
      setRecordingState('recording');

      // Resume timer with remaining time
      if (remainingTimeRef.current && remainingTimeRef.current > 0) {
        recordingTimerRef.current = setTimeout(() => {
          // Pause recording for preparation
          if (recorder.state === 'recording') {
            recorder.pause();
          }
          setIsRecording(false);
          remainingTimeRef.current = null;

          // Mark segment end time
          const endTime = Date.now();
          const lastMarker = mediaRecorderRef.current.segmentMarkers[mediaRecorderRef.current.segmentMarkers.length - 1];
          if (lastMarker && !lastMarker.endTime) {
            lastMarker.endTime = endTime;
            lastMarker.duration = endTime - lastMarker.startTime;
          }

          startPreparationCountdown(currentSegmentIndex);
        }, remainingTimeRef.current);
      }
    }
  };

  // Stop recording
  const stopRecordingFlow = async () => {
    // Clear all timers
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (preparationTimerRef.current) {
      clearInterval(preparationTimerRef.current);
      preparationTimerRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // Stop recorder if active
    const recorder = mediaRecorderRef.current?.recorder;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }

    setIsRecording(false);
    setIsPaused(false);
    setCountdown(null);
    setPreparationCountdown(null);
    setRecordingState('completed'); // Set state to completed
    remainingTimeRef.current = null;
  };

  // Cleanup on unmount or tab switch
  useEffect(() => {
    return () => {
      stopCamera();
      stopRecordingFlow();
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'golive') {
      stopCamera();
    }
  }, [activeTab]);

  // Auto-start camera in mobile record mode
  useEffect(() => {
    if (isMobileRecordMode && activeTab === 'golive' && !isCameraActive && !cameraError) {
      const timer = setTimeout(() => {
        startCamera();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMobileRecordMode, activeTab]);

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: isMobileRecordMode ? '#000000' : '#f8f9fa'
    }}>
      {/* Tab Navigation - Hidden in mobile record mode */}
      {!isMobileRecordMode && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          padding: '1.5rem 2rem 1rem',
          background: '#f8f9fa',
          flexShrink: 0
        }}>
          <button
            onClick={() => setActiveTab('preview')}
            style={{
              padding: '0.5rem 0',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'preview' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'preview' ? '#3b82f6' : '#6b7280',
              fontSize: 'var(--font-sm)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab('golive')}
            style={{
              padding: '0.5rem 0',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'golive' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'golive' ? '#3b82f6' : '#6b7280',
              fontSize: 'var(--font-sm)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Go Live
          </button>
        </div>
      )}

      {/* Tab Content - Scrollable */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'preview' && !isMobileRecordMode ? (
          <ThreadsMobilePreview
            content={content}
            accountName={accountName}
            loading={loading}
            showButtons={hasContent}
            onEdit={onEdit}
            onPublish={onPublish}
          />
        ) : (
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '0' : '2rem'
          }}>
            {/* Phone Mockup - 9:16 vertical (cropping horizontal camera) */}
            <div style={{
              width: isMobile ? '100%' : '360px',
              height: isMobile ? '100%' : '640px',
              background: '#000000',
              borderRadius: isMobile ? '0' : '40px',
              border: isMobile ? 'none' : '12px solid #1e293b',
              boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Phone Notch - Only show on desktop */}
              {!isMobile && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '150px',
                  height: '30px',
                  background: '#1e293b',
                  borderBottomLeftRadius: '20px',
                  borderBottomRightRadius: '20px',
                  zIndex: 10
                }} />
              )}

              {/* Camera View Area */}
              <div style={{
                flex: 1,
                position: 'relative',
                background: '#000000ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {(() => {
                  console.log('Display condition check:', {
                    recordedVideoUrl: recordedVideoUrl ? 'exists' : 'null',
                    recordingState,
                    isCameraActive,
                    cameraError
                  });
                  return null;
                })()}
                {recordedVideoUrl && recordingState === 'completed' ? (
                  // Show recorded video with auto-generated subtitles
                  (() => {
                    console.log('✅ Rendering recorded video section');
                    return (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: '#000000',
                        zIndex: 100
                      }}>
                        <video
                      ref={videoPlaybackRef}
                      src={recordedVideoUrl}
                      controls
                      onTimeUpdate={(e) => {
                        // Update subtitle based on current playback time
                        const currentTime = e.target.currentTime * 1000; // Convert to milliseconds
                        const videoDuration = e.target.duration * 1000; // Total video duration in ms

                        // Get segment data - prefer markers, fallback to scriptSegments
                        let segments = savedSegmentMarkers.length > 0 ? savedSegmentMarkers : (mediaRecorderRef.current?.segmentMarkers || []);
                        if (segments.length === 0 && scriptSegments.length > 0) {
                          segments = scriptSegments;
                        }

                        if (segments.length === 0) return;

                        // Calculate segment duration based on video length if using scriptSegments
                        const useEstimatedDuration = savedSegmentMarkers.length === 0 && mediaRecorderRef.current?.segmentMarkers?.length === 0;
                        const segmentDurationPerSegment = useEstimatedDuration ? videoDuration / segments.length : null;

                        // Find which segment the current playback time falls into
                        let cumulativeDuration = 0;
                        for (let i = 0; i < segments.length; i++) {
                          const segment = segments[i];
                          // Use actual duration if available, otherwise estimate based on video length
                          const segmentDuration = useEstimatedDuration ? segmentDurationPerSegment : (segment.duration || (segment.text?.length / 3 * 1000) || 3000);

                          if (currentTime >= cumulativeDuration && currentTime < cumulativeDuration + segmentDuration) {
                            if (playbackSegmentIndex !== i) {
                              setPlaybackSegmentIndex(i);
                            }
                            return;
                          }

                          cumulativeDuration += segmentDuration;
                        }

                        // If we're past all segments, show the last one
                        if (currentTime >= cumulativeDuration && segments.length > 0) {
                          const lastIndex = segments.length - 1;
                          if (playbackSegmentIndex !== lastIndex) {
                            setPlaybackSegmentIndex(lastIndex);
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'  // Use 'contain' to show the full 9:16 video without cropping
                      }}
                    />

                    {/* Subtitle overlay on video - Auto subtitle from current segment */}
                    {(() => {
                      // Try segment markers first, then fall back to scriptSegments
                      let segmentMarkers = savedSegmentMarkers.length > 0 ? savedSegmentMarkers : (mediaRecorderRef.current?.segmentMarkers || []);

                      // If no segment markers, use scriptSegments as fallback
                      if (segmentMarkers.length === 0 && scriptSegments.length > 0) {
                        segmentMarkers = scriptSegments;
                      }

                      const currentSegment = segmentMarkers[playbackSegmentIndex];

                      // Debug: log subtitle state
                      console.log('📺 Subtitle state:', {
                        hasSavedMarkers: savedSegmentMarkers.length > 0,
                        hasRefMarkers: mediaRecorderRef.current?.segmentMarkers?.length > 0,
                        hasScriptSegments: scriptSegments.length > 0,
                        totalMarkers: segmentMarkers.length,
                        playbackIndex: playbackSegmentIndex,
                        currentSegment: currentSegment
                      });

                      if (!currentSegment) {
                        console.log('⚠️ No current segment for subtitle');
                        return null;
                      }

                      return (
                        <div style={{
                          position: 'absolute',
                          bottom: '12%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '90%',
                          textAlign: 'center',
                          pointerEvents: 'none',
                          zIndex: 101
                        }}>
                          <div style={{
                            display: 'inline-block',
                            padding: '0.4rem 0.8rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '6px',
                            maxWidth: '95%'
                          }}>
                            <span style={{
                              fontFamily: "'Microsoft JhengHei', 'Microsoft YaHei', sans-serif",
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: '#ffffff',
                              lineHeight: '1.4',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'keep-all',
                              display: 'block',
                              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                            }}>
                              {currentSegment.text}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                      </div>
                    );
                  })()
                ) : cameraError ? (
                  // Error State
                  <div style={{
                    textAlign: 'center',
                    color: '#ef4444',
                    padding: '2rem'
                  }}>
                    <Icon icon="mdi:camera-off" style={{ fontSize: '3rem', marginBottom: '1rem' }} />
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>{cameraError}</p>
                  </div>
                ) : isCameraActive ? (
                  // Camera Feed cropped to 9:16 vertical (from horizontal camera)
                  <div style={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                  }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',  // Show full 9:16 vertical video
                        transform: 'scaleX(-1)' // Mirror the video like a selfie camera
                      }}
                    />
                    {/* Recording Format Indicator */}
                  </div>
                ) : (
                  // Placeholder
                  <div style={{
                    textAlign: 'center',
                    color: '#64748b'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>點擊下方按鈕開始錄製</p>
                  </div>
                )}

                {/* Countdown Overlay */}
                {countdown !== null && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 20
                  }}>
                    <div style={{
                      fontSize: '6rem',
                      fontWeight: '700',
                      color: '#ffffff',
                      animation: 'countdownPulse 1s ease-out'
                    }}>
                      {countdown}
                    </div>
                  </div>
                )}

                {/* Teleprompter Overlay - Show during recording, paused, or preparing */}
                {(() => {
                  const shouldShow = (recordingState === 'recording' || recordingState === 'paused' || recordingState === 'preparing') && scriptSegments.length > 0;

                  if (!shouldShow) return null;

                  // Show next line during preparation countdown
                  const displayIndex = recordingState === 'preparing' ? currentSegmentIndex + 1 : currentSegmentIndex;

                  // Don't show if we're past the last segment
                  if (displayIndex >= scriptSegments.length) return null;

                  const isPreparing = recordingState === 'preparing';

                  return (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '85%',
                      background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.7))',
                      padding: '2rem 1.5rem',
                      zIndex: 15,
                      borderRadius: '16px',
                      maxHeight: '60%',
                      overflow: 'hidden'
                    }}>
                      {/* Voice activity indicator - Show during recording */}
                      {recordingState === 'recording' && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          marginBottom: '1rem'
                        }}>
                          <Icon
                            icon={isSpeaking ? "mdi:microphone" : "mdi:microphone-off"}
                            style={{
                              fontSize: '1.5rem',
                              color: isSpeaking ? '#10b981' : '#9ca3af',
                              transition: 'all 0.2s ease'
                            }}
                          />
                          <span style={{
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: isSpeaking ? '#10b981' : '#9ca3af',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            {isSpeaking ? '🎤 偵測到語音' : '等待語音...'}
                          </span>
                        </div>
                      )}

                      {/* Hint text when preparing */}
                      {isPreparing && (
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#fb923c',
                          marginBottom: '1rem',
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          下一句 ↓
                        </div>
                      )}

                      {/* Current/Next Line - Only show one line */}
                      <div style={{
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: '#ffffff',
                        lineHeight: '1.8',
                        textAlign: 'center',
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)'
                      }}>
                        {scriptSegments[displayIndex]?.text || '載入中...'}
                      </div>
                    </div>
                  );
                })()}

                {/* Preparation Countdown - Top right corner */}
                {isPreparingNext && preparationCountdown !== null && (
                  <div style={{
                    position: 'absolute',
                    top: '60px',
                    right: '15px',
                    background: 'rgba(251, 146, 60, 0.95)',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 30,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                  }}>
                    <div style={{
                      fontSize: '1.75rem',
                      fontWeight: '700',
                      color: '#ffffff'
                    }}>
                      {preparationCountdown}
                    </div>
                    <div style={{
                      fontSize: '0.5rem',
                      color: '#ffffff',
                      marginTop: '0.1rem'
                    }}>
                      準備
                    </div>
                  </div>
                )}

                {/* Recording Indicator */}
                {isRecording && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(239, 68, 68, 0.9)',
                    color: '#ffffff',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    zIndex: 5
                  }}>
                    <div className="pulse-dot" style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#ffffff'
                    }} />
                    錄製中 {currentSegmentIndex + 1}/{scriptSegments.length}
                  </div>
                )}
              </div>

              {/* Bottom Control Bar */}
              <div style={{
                padding: '1.5rem',
                background: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                minHeight: '100px'  // Ensure minimum height for visibility
              }}>
                {(() => {
                  // Debug: log current state
                  console.log('🎬 Recording state:', recordingState, 'Script segments:', scriptSegments.length);

                  if (recordingState === 'completed') {
                    return (
                      // Reset button after completion
                      <button
                        onClick={() => {
                          setRecordingState('idle');
                          setRecordedVideoUrl(null);
                          setCurrentSegmentIndex(0);
                        }}
                        style={{
                          padding: '0.8rem 1.6rem',
                          background: '#3b82f6',
                          border: 'none',
                          borderRadius: '12px',
                          color: '#ffffff',
                          fontSize: '1rem',
                          fontWeight: '400',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        開始錄製
                      </button>
                    );
                  } else if (recordingState === 'idle' || !recordingState) {
                    return (
                      // Start recording button - Always show when idle
                      <button
                        onClick={startRecordingFlow}
                        disabled={scriptSegments.length === 0}
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          background: '#ffffff',
                          border: 'none',
                          cursor: scriptSegments.length > 0 ? 'pointer' : 'not-allowed',
                          padding: 0,
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          if (scriptSegments.length > 0) {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: scriptSegments.length > 0 ? 'rgb(255, 56, 69)' : '#9ca3af',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }} />
                      </button>
                    );
                  }
                  // For recording/paused/preparing/countdown states, show nothing (stop button is below)
                  return null;
                })()}
              </div>
            </div>

            {/* Stop Button - Outside Phone UI, below phone */}
            {(recordingState === 'recording' || recordingState === 'paused' || recordingState === 'preparing' || recordingState === 'countdown') && (
              <div style={{
                marginTop: '2rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <button
                  onClick={stopRecordingFlow}
                  style={{
                    padding: '1rem 3rem',
                    background: '#ef4444',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 6px 30px rgba(239, 68, 68, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(239, 68, 68, 0.4)';
                  }}
                >
                  <Icon
                    icon="mdi:stop-circle"
                    style={{
                      fontSize: '1.5rem',
                      color: '#ffffff'
                    }}
                  />
                  結束錄影
                </button>
              </div>
            )}

            {/* Action Buttons - Below Phone (only show when completed) */}
            {recordedVideoUrl && recordingState === 'completed' && (
              <div style={{
                marginTop: '1.5rem',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => {
                    // Call the onRecordingComplete callback with video data
                    if (onRecordingComplete) {
                      const videoBlob = recordedVideoUrl ? fetch(recordedVideoUrl).then(r => r.blob()) : null;
                      onRecordingComplete({
                        videoUrl: recordedVideoUrl,
                        aspectRatio: aspectRatio,
                        blob: videoBlob
                      });
                    }
                  }}
                  style={{
                    padding: '1rem 2.5rem',
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.4)';
                  }}
                >
                  <Icon icon="mdi:check-circle" style={{ fontSize: '1.5rem' }} />
                  確認完成
                </button>
                <button
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = recordedVideoUrl;
                    a.download = `reels-9x16-${Date.now()}.webm`;
                    a.click();
                  }}
                  style={{
                    padding: '1rem 2rem',
                    background: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  <Icon icon="mdi:download" style={{ fontSize: '1.5rem' }} />
                  下載影片
                </button>
                <button
                  onClick={() => {
                    setRecordedVideoUrl(null);
                    setRecordingState('idle');
                    setCurrentSegmentIndex(0);
                  }}
                  style={{
                    padding: '1rem 2rem',
                    background: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.3)';
                  }}
                >
                  <Icon icon="mdi:reload" style={{ fontSize: '1.5rem' }} />
                  重新錄製
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }
          .pulse-dot {
            animation: pulse 1.5s ease-in-out infinite;
          }
          @keyframes countdownPulse {
            0% {
              transform: scale(0.5);
              opacity: 0;
            }
            50% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
}

export default PreviewTabs;
